'use client';

/**
 * LES 24 LANGUES AUTOUR DES 5 CONTINENTS — vitrine royale.
 * Cinq cartes continentales (Afrique, Asie, Europe, Amériques, Océanie) avec
 * les langues de traduction de chaque continent (citation croisée autorisée),
 * bannière dorée générée et compteurs par continent.
 */
import Image from 'next/image';
import { useApp } from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import { EDITIONS } from '@/lib/quran-data';
import { CONTINENTS, LANGS_BY_CONTINENT } from '@/data/continents';
import { Globe2 } from 'lucide-react';

export default function ContinentLanguages() {
  const { uiLang } = useApp();
  const t = DICTS[uiLang];

  return (
    <section aria-label={t.continents.title} className="relative py-14">
      {/* Bannière latérale dorée */}
      <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
        <div className="relative mb-10 overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.3)]">
          <Image
            src="/images/banners/languages_banner.png"
            alt=""
            width={1440}
            height={704}
            unoptimized
            className="h-[170px] w-full object-cover sm:h-[220px]"
          />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(3,32,25,0.94),rgba(3,32,25,0.35))]" aria-hidden />
          <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-10">
            <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.34em] text-[#f0d98c]/90 sm:text-[11px]">
              {t.continents.eyebrow}
            </p>
            <h2 className="font-serif-royal max-w-xl text-2xl font-bold leading-tight sm:text-3xl">
              <span className="text-gold-gradient">{t.continents.title}</span>
            </h2>
            <p className="mt-2 hidden max-w-xl text-[12.5px] leading-relaxed text-[#cfe0cf]/85 sm:block">
              {t.continents.sub}
            </p>
          </div>
        </div>

        {/* 5 continents */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CONTINENTS.map((c) => {
            const ids = LANGS_BY_CONTINENT[c.key];
            const langs = ids
              .map((id) => EDITIONS.find((e) => e.id === id))
              .filter(Boolean);
            return (
              <article
                key={c.key}
                className="glass-gold group relative overflow-hidden rounded-2xl p-5"
                style={{ borderColor: `${c.color}44` }}
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute -right-4 -top-8 font-serif-royal text-[96px] leading-none opacity-[0.05]"
                >
                  {c.emoji}
                </span>
                <header className="mb-3 flex items-center gap-3">
                  <span
                    className="flex h-11 w-11 items-center justify-center rounded-xl border text-xl"
                    style={{ borderColor: `${c.color}66`, backgroundColor: `${c.color}18` }}
                  >
                    {c.emoji}
                  </span>
                  <span>
                    <h3 className="font-serif-royal text-lg font-bold" style={{ color: c.color }}>
                      {c.label[uiLang]}
                    </h3>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#cfe0cf]/55">
                      {langs.length} {t.continents.langs_label}
                    </p>
                  </span>
                </header>
                <p className="mb-3 text-[12px] leading-relaxed text-[#cfe0cf]/70">{c.blurb[uiLang]}</p>
                <ul className="flex flex-wrap gap-1.5">
                  {langs.map((l) => (
                    <li
                      key={l!.id}
                      className="flex items-center gap-1 rounded-full border border-[rgba(212,175,55,0.28)] bg-[rgba(212,175,55,0.07)] px-2.5 py-1 text-[11px] font-semibold text-[#e8dcc0]"
                      title={l!.name}
                    >
                      <span aria-hidden>{l!.flag}</span>
                      <span dir={l!.rtl ? 'rtl' : undefined}>{l!.native}</span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>

        {/* Note objectif 1000 langues */}
        <p className="mx-auto mt-8 flex max-w-2xl items-center justify-center gap-2 text-center text-xs text-[#cfe0cf]/55">
          <Globe2 className="h-4 w-4 shrink-0 text-[#d4af37]/70" aria-hidden />
          {t.continents.sub}
        </p>
      </div>
    </section>
  );
}
